import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { downloadBlob, filenameFromResponse } from "@/lib/resumeUtils";
import TranslationEditor from "@/components/letters/TranslationEditor";
import { X, Save, FileDown, Plus, Trash2, ClipboardCheck, BookMarked, Package, Maximize2, Minimize2, Check, Sparkles, Video, Loader2, ExternalLink, RefreshCw, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { MatchScoreBadge } from "./MatchScoreBadge";
import { MatchScoreExplainer } from "./MatchScoreExplainer";
import ImproveMatchWizard from "./ImproveMatchWizard";
import { useConfirm } from "@/components/ConfirmDialog";
import { stashPendingJobHint } from "@/components/PendingJobHint";
import VideoLetterModal from "@/components/VideoLetterModal";

const KIND_LABEL = {
  letter: { nl: "Motivatiebrief", en: "Motivation-Letter", de: "Motivationsschreiben", fr: "Lettre-Motivation", es: "Carta-Motivacion" },
  validation: { nl: "Eisen-Validatie", en: "Requirement-Validation", de: "Anforderungsabgleich", fr: "Validation-Exigences", es: "Validacion-Requisitos" },
  package: { nl: "Sollicitatiepakket", en: "Application-Package", de: "Bewerbungspaket", fr: "Dossier-Candidature", es: "Paquete-Candidatura" },
  resume: { nl: "CV", en: "Resume", de: "Lebenslauf", fr: "CV", es: "CV" },
};

// Slugify a company/job-title to match the backend's `_slugify_part`:
// strip accents, keep [A-Za-z0-9], collapse other runs to single hyphens.
const _slug = (s) => {
  if (!s) return "";
  const noAccents = s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  return noAccents.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
};

// Fallback filename builder — mirrors the backend `_build_filename` so
// even when Content-Disposition is stripped by a proxy the download
// still gets a vacancy-traceable name.
const _fallbackFilename = (letter, kind, lang, ext) => {
  const company = _slug(letter?.company);
  const title = _slug(letter?.job_title);
  const label = KIND_LABEL[kind]?.[lang] || KIND_LABEL[kind]?.en || kind;
  const langCode = (lang || "nl").toUpperCase();
  const parts = [company, title, label, langCode].filter(Boolean);
  if (!company && !title) return `Sollicitatie_${label}_${langCode}.${ext}`;
  return `${parts.join("_")}.${ext}`;
};

function AutoGrowTextarea({ value, onChange, testId, recalcKey, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + 2}px`;
    }
  }, [value, recalcKey]);
  return (
    <textarea
      ref={ref}
      rows={2}
      data-testid={testId}
      className={`ed-input ${className}`}
      style={{ overflow: "hidden", resize: "none" }}
      value={value}
      onChange={onChange}
    />
  );
}

export default function LetterEditor({ letterId, onClose, onRegistered, autoOpenImprove = false, onNavigateToCvs }) {
  const { t, lang } = useT();
  const confirm = useConfirm();
  const [letter, setLetter] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dlBusy, setDlBusy] = useState(null); // "resume" | "validation" | "letter"
  const [registering, setRegistering] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  // Attached video (letter-specific OR elevator-pitch fallback) — powers the
  // "N keer bekeken" analytics badge next to the video CTA so users see how
  // often recruiters actually clicked their QR / share link.
  const [videoMeta, setVideoMeta] = useState(null);
  const refreshVideoMeta = useCallback(async () => {
    try {
      const r = await api.get(`/video-letters/by-letter/${letterId}`);
      const first = r.data?.[0] || null;
      if (first) {
        setVideoMeta({ ...first, source: "letter" });
        return;
      }
      // Fallback — elevator pitch is shown on the letter PDF when no
      // letter-specific video is recorded. Same endpoint used by CVPanel.
      const p = await api.get("/video-letters/elevator-pitch");
      setVideoMeta(p.data ? { ...p.data, source: "elevator" } : null);
    } catch {
      setVideoMeta(null);
    }
  }, [letterId]);
  useEffect(() => { if (letterId) refreshVideoMeta(); }, [letterId, refreshVideoMeta]);
  // Detect whether the letter is currently linked to a DEFAULT CV. If
  // yes, we surface a "Ontkoppel" banner so the user can fork the CV
  // into a vacancy-specific variant — future edits to the default CV
  // (or match-rewrites for other letters) then don't leak into this
  // letter's downloads.
  const [linkedIsDefault, setLinkedIsDefault] = useState(false);
  const [detachBusy, setDetachBusy] = useState(false);
  // Track regen-in-progress from the enrich-box so we can dim the letter
  // preview + show an unmistakable status pill above it. Without this the
  // user only saw the small button spinner and thought nothing was happening.
  const [regenBusy, setRegenBusy] = useState(false);
  useEffect(() => {
    if (!letter?.resume_id) { setLinkedIsDefault(false); return; }
    let cancel = false;
    api.get(`/resumes/${letter.resume_id}`)
      .then(({ data }) => { if (!cancel) setLinkedIsDefault(!!data.is_default); })
      .catch(() => { if (!cancel) setLinkedIsDefault(false); });
    return () => { cancel = true; };
  }, [letter?.resume_id]);
  const detachResume = async () => {
    if (!letter?.id) return;
    // Styled in-app confirm — the previous browser-native `window.confirm`
    // was jarring visually and didn't match the sheet's design language.
    const ok = await confirm({
      title: t("ltDetachCta"),
      body: t("ltDetachConfirm"),
      confirmLabel: t("ltDetachCta"),
      tone: "info",
    });
    if (!ok) return;
    setDetachBusy(true);
    try {
      const { data } = await api.post(`/letters/${letter.id}/detach-resume`);
      if (data?.letter) setLetter(data.letter);
      didChangeRef.current = true;
      toast.success(t("ltDetachSuccess", { name: data?.resume?.name || "" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ltDetachFailed"));
    } finally {
      setDetachBusy(false);
    }
  };
  const letterRef = useRef(null);
  letterRef.current = letter;
  // Tracks whether the letter (or its data) actually mutated during this
  // sheet session. Closing without any mutation should NOT trigger a list
  // reload on the parent — that visible refresh was jarring for users.
  const didChangeRef = useRef(false);
  const closeSheet = useCallback(() => {
    onClose?.(didChangeRef.current);
  }, [onClose]);

  useEffect(() => {
    api.get(`/letters/${letterId}`)
      .then(({ data }) => {
        setLetter(data);
        // Auto-open the improve wizard when the parent asks for it (e.g. the
        // "Verbeter"-CTA in the letters list — one-click into the flow).
        if (autoOpenImprove) setImproveOpen(true);
      })
      .catch(() => { toast.error(t("ltLoadFailed")); onClose?.(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterId]);

  const update = useCallback((patch) => {
    setLetter((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const save = useCallback(async (cur) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/letters/${letterId}`, {
        letter_text: cur.letter_text,
        validation_items: cur.validation_items,
      });
      setLetter((prev) => (prev === cur ? data : prev));
      if (letterRef.current === cur) setDirty(false);
      didChangeRef.current = true;
      return true;
    } catch {
      toast.error(t("ltSaveFailed"));
      return false;
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterId, t]);

  // Auto-save: debounce 1.2s after the last change
  useEffect(() => {
    if (!dirty || !letter) return;
    const timer = setTimeout(() => { save(letter); }, 1200);
    return () => clearTimeout(timer);
  }, [letter, dirty, save]);

  // Download language picker — user chooses NL or EN at download time.
  // Defaults to the letter's original language. If they pick another,
  // the backend translates the letter body on-the-fly (cached per lang
  // on the letter doc so repeat downloads are instant).
  const [dlLang, setDlLang] = useState(null);
  // Side-by-side translation editor — opened when the user wants to
  // tweak the AI translation for a given target language before
  // freezing it as the download version.
  const [translationEditorLang, setTranslationEditorLang] = useState(null);
  useEffect(() => {
    if (letter?.lang && dlLang === null) {
      // Accept any of the supported download languages as the initial
      // picker value; unknown codes fall back to NL.
      const norm = String(letter.lang).toLowerCase();
      setDlLang(["nl", "en", "de"].includes(norm) ? norm : "nl");
    }
  }, [letter?.lang, dlLang]);
  const activeDlLang = dlLang || letter?.lang || "nl";

  const download = async (kind) => {
    setDlBusy(kind);
    try {
      if (dirty && kind !== "resume") {
        const ok = await save(letterRef.current);
        if (!ok) return;
      }
      const dl = activeDlLang;
      if (kind === "resume") {
        // Passing `letter_id` lets the backend align the CV's professional
        // title with the vacancy on-the-fly. Stored CV is never mutated.
        const res = await api.get(`/resumes/${letter.resume_id}/pdf?lang=${dl}&letter_id=${letterId}`, { responseType: "blob" });
        const name = _fallbackFilename(letter, "resume", dl, "pdf");
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), name);
      } else if (kind === "package") {
        const res = await api.get(`/letters/${letterId}/package?lang=${dl}`, { responseType: "blob" });
        const name = filenameFromResponse(res, _fallbackFilename(letter, "package", dl, "pdf"));
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), name);
      } else if (kind === "package-zip") {
        // Bilingual (NL + EN) ZIP. Backend accepts up to 3 langs but
        // this button intentionally ships the "beide talen" bundle.
        const res = await api.get(`/letters/${letterId}/package.zip?langs=nl,en`, { responseType: "blob" });
        const zipFallback = (() => {
          const stub = [_slug(letter?.company), _slug(letter?.job_title)].filter(Boolean).join("_") || "Sollicitatie";
          return `${stub}_Sollicitatiepakket_NL+EN.zip`;
        })();
        const name = filenameFromResponse(res, zipFallback);
        downloadBlob(new Blob([res.data], { type: "application/zip" }), name);
      } else {
        const res = await api.get(`/letters/${letterId}/pdf?doc=${kind}&lang=${dl}`, { responseType: "blob" });
        const name = filenameFromResponse(res, _fallbackFilename(letter, kind, dl, "pdf"));
        downloadBlob(new Blob([res.data], { type: "application/pdf" }), name);
      }
    } catch (err) {
      const detail = err?.response?.status === 404 && kind === "resume"
        ? t("ltDlResumeMissing")
        : err?.response?.data?.detail || t("rbPdfFailed");
      toast.error(detail);
    } finally {
      setDlBusy(null);
    }
  };

  const toRegister = async () => {
    setRegistering(true);
    try {
      const { data } = await api.post(`/letters/${letterId}/to-register`);
      update({ job_id: data.job.id });
      setDirty(false);
      toast.success(t("ltToRegisterOk"));
      onRegistered?.();
    } catch {
      toast.error(t("ltToRegisterFailed"));
    } finally {
      setRegistering(false);
    }
  };

  if (!letter) {
    return (
      <div data-testid="letter-editor">
        <div className="ed-sheet-overlay" onClick={closeSheet} />
        <div className="ed-sheet p-10"><p className="ed-eyebrow">{t("loading")}</p></div>
      </div>
    );
  }

  const setValItem = (i, value) =>
    update({ validation_items: letter.validation_items.map((x, idx) => (idx === i ? value : x)) });

  return (
    <div data-testid="letter-editor">
      <div className="ed-sheet-overlay" onClick={closeSheet} />
      <div className={`ed-sheet ${maximized ? "ed-sheet-max" : ""}`}>
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-neutral-200 px-8 py-5 flex items-start justify-between z-10">
          <div className="max-w-[70%]">
            <p className="ed-eyebrow">{t("ltLetter")}</p>
            <h2 className="ed-h2 mt-1 leading-tight">{letter.job_title}</h2>
            <p className="font-serif text-lg text-neutral-700 italic mt-1">
              {letter.company} · {letter.lang.toUpperCase()} · {t("ltBasedOn")} “{letter.resume_name}”
            </p>
            {/* Direct-to-vacancy CTA so the user can jump to the
                employer's application page (upload CV + letter there)
                without first going back to the applications overview.
                Only rendered when a `job_url` was captured on the
                letter — freshly-typed letters without a saved URL
                simply hide the button. */}
            {letter.job_url && (
              <a
                href={letter.job_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="letter-editor-open-vacancy"
                className="mt-3 inline-flex items-center gap-1.5 border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-800 px-3 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] transition-colors"
                title={t("ltOpenVacancyHint")}
              >
                <ExternalLink size={12} strokeWidth={1.75} />
                {t("ltOpenVacancy")}
              </a>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              data-testid="letter-editor-maximize"
              onClick={() => setMaximized(!maximized)}
              title={maximized ? t("ltMinimize") : t("ltMaximize")}
              className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            >
              {maximized ? <Minimize2 size={16} strokeWidth={1.5} /> : <Maximize2 size={16} strokeWidth={1.5} />}
            </button>
            <button
              data-testid="letter-editor-close"
              onClick={closeSheet}
              className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-8">
          {/* Detach banner — the letter is linked to a DEFAULT CV so any
              edit on the default (either manual or via another letter's
              improve-match) would leak into this letter's downloads.
              One click forks the current CV into a vacancy-scoped
              variant, freezing the download package. */}
          {linkedIsDefault && (
            <div
              data-testid="letter-detach-banner"
              className="border border-amber-400 bg-amber-50/70 p-4 flex items-start gap-3"
            >
              <Sparkles size={16} strokeWidth={1.5} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-base leading-snug">{t("ltDetachTitle")}</p>
                <p className="text-sm text-neutral-700 mt-1 leading-relaxed">
                  {t("ltDetachBody", { name: letter.resume_name || "" })}
                </p>
              </div>
              <button
                type="button"
                onClick={detachResume}
                disabled={detachBusy}
                data-testid="letter-detach-btn"
                className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-amber-800 bg-amber-800 text-white hover:bg-amber-700 disabled:opacity-40 px-3 py-2 shrink-0"
              >
                {detachBusy ? t("ltDetachBusy") : t("ltDetachCta")}
              </button>
            </div>
          )}

          {/* Match score */}
          {(letter.match_score !== null && letter.match_score !== undefined) && (
            <div className="ed-card p-5 flex items-start gap-5" data-testid="letter-match-block">
              <div className="flex items-center shrink-0">
                <MatchScoreBadge score={letter.match_score} size="lg" />
                <MatchScoreExplainer
                  score={letter.match_score}
                  onOpenWizard={() => {
                    if (!letter.match_wizard_opened_at) {
                      const ts = new Date().toISOString();
                      setLetter((prev) => prev ? { ...prev, match_wizard_opened_at: ts } : prev);
                      api.post(`/letters/${letterId}/mark-wizard-opened`).catch(() => {});
                    }
                    setImproveOpen(true);
                  }}
                  onNavigateToCvs={onNavigateToCvs ? () => {
                    // Preserve vacancy context after leaving the letter editor
                    // by surfacing job title + AI match summary in a toast the
                    // user reads on the CVs view. Also stash a persistent hint
                    // so a subtle "Ontbreekt de juiste variant?"-block can
                    // render on the CVs overview.
                    const jobTitle = letter.job_title || "";
                    const description = letter.match_summary || t("mseNavToastFallback");
                    stashPendingJobHint({
                      jobTitle,
                      jobDescription: letter.job_description || "",
                      matchSummary: letter.match_summary || "",
                    });
                    toast(t("mseNavToastTitle", { job: jobTitle }), {
                      description,
                      duration: 8000,
                    });
                    onNavigateToCvs();
                  } : null}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="ed-eyebrow">{t("ltMatchScore")}</p>
                {letter.match_summary ? (
                  <p className="font-serif text-base text-neutral-800 mt-1 leading-relaxed" data-testid="letter-match-summary">
                    {letter.match_summary}
                  </p>
                ) : (
                  <p className="text-sm text-neutral-500 italic mt-1">{t("ltMatchScoreHint")}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                {(() => {
                  const notImprovedYet = !(letter.match_history && letter.match_history.length > 0);
                  const wizardNotOpened = !letter.match_wizard_opened_at;
                  const canImprove = letter.match_score !== null && letter.match_score !== undefined && letter.match_score < 100;
                  const shouldPulse = wizardNotOpened && notImprovedYet && canImprove;
                  return (
                    <button
                      data-testid="letter-improve-match"
                      type="button"
                      onClick={() => {
                        // Optimistically mark wizard as opened so the pulse
                        // stops the moment the user clicks, then persist to
                        // the backend (fire-and-forget: harmless if it fails).
                        if (!letter.match_wizard_opened_at) {
                          const ts = new Date().toISOString();
                          setLetter((prev) => prev ? { ...prev, match_wizard_opened_at: ts } : prev);
                          api.post(`/letters/${letterId}/mark-wizard-opened`).catch(() => {});
                        }
                        setImproveOpen(true);
                      }}
                      className={shouldPulse ? "ed-btn ed-btn-pulse self-end" : "ed-btn self-end"}
                    >
                      <Sparkles size={14} strokeWidth={1.75} /> {t("ltImproveCta")}
                    </button>
                  );
                })()}
                <MatchAiUsageHint />
              </div>
            </div>
          )}
          {letter.match_history && letter.match_history.length > 0 && (
            <div className="ed-card p-4" data-testid="letter-match-history">
              <p className="ed-eyebrow mb-3">{t("ltMatchHistoryTitle")}</p>
              <ol className="space-y-1.5">
                {[...letter.match_history].reverse().slice(0, 5).map((h, idx) => {
                  const delta = (h.new ?? 0) - (h.previous ?? 0);
                  return (
                    <li key={idx} className="flex items-center gap-3 text-sm font-mono" data-testid={`letter-match-history-${idx}`}>
                      <span className="text-neutral-500 text-[0.65rem] uppercase tracking-[0.15em] w-24">
                        {new Date(h.at).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL")}
                      </span>
                      <span className="text-neutral-500">{h.previous ?? "—"}%</span>
                      <span className="text-neutral-400">→</span>
                      <span className="font-semibold">{h.new}%</span>
                      <span className={`text-[0.65rem] px-1.5 py-0.5 border ${delta > 0 ? "border-emerald-700 bg-emerald-50 text-emerald-800" : delta < 0 ? "border-rose-700 bg-rose-50 text-rose-800" : "border-neutral-300 text-neutral-500"}`}>
                        {delta > 0 ? "+" : ""}{delta} pt
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Downloads + actions */}
          <div className="ed-card p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="ed-eyebrow flex items-center gap-2">
                <FileDown size={12} strokeWidth={1.5} /> {t("ltDownloads")}
              </p>
              {/* NL / EN toggle — applies to every button in this block.
                  Defaults to the letter's original language; picking the
                  other one triggers server-side AI translation of the
                  letter body (cached per lang on the letter doc). */}
              <div
                data-testid="letter-dl-lang"
                className="inline-flex items-stretch border border-neutral-300"
                role="group"
                aria-label={t("ltDlLangLabel")}
              >
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 flex items-center px-2.5 border-r border-neutral-300">
                  {t("ltDlLangLabel")}
                </span>
                {[
                  { code: "nl", label: "NL" },
                  { code: "en", label: "EN" },
                  { code: "de", label: "DE" },
                ].map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setDlLang(code)}
                    aria-pressed={activeDlLang === code}
                    data-testid={`letter-dl-lang-${code}`}
                    className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-2.5 py-1.5 transition-colors ${
                      activeDlLang === code
                        ? "bg-neutral-900 text-[#F9F8F6]"
                        : "bg-transparent text-neutral-600 hover:text-neutral-900"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {activeDlLang !== (letter?.lang || "nl") && (
              <div
                data-testid="letter-dl-lang-notice"
                className="flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 px-3 py-2"
              >
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-amber-800 flex-1 min-w-[200px]">
                  {t("ltDlLangTranslateNotice", {
                    from: (letter?.lang || "nl").toUpperCase(),
                    to: activeDlLang.toUpperCase(),
                  })}
                </p>
                <button
                  type="button"
                  onClick={() => setTranslationEditorLang(activeDlLang)}
                  data-testid="letter-dl-lang-edit-translation"
                  className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-amber-800 bg-amber-800 text-white hover:bg-amber-700 px-3 py-1.5"
                >
                  {t("ltDlLangEditTranslation")}
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button data-testid="letter-dl-resume" className="ed-btn ed-btn-outline" onClick={() => download("resume")} disabled={!!dlBusy}>
                <FileDown size={14} strokeWidth={1.5} /> {dlBusy === "resume" ? t("ltDlBusy") : t("ltDlResume")}
              </button>
              <button data-testid="letter-dl-validation" className="ed-btn ed-btn-outline" onClick={() => download("validation")} disabled={!!dlBusy}>
                <ClipboardCheck size={14} strokeWidth={1.5} /> {dlBusy === "validation" ? t("ltDlBusy") : t("ltDlValidation")}
              </button>
              <button data-testid="letter-dl-letter" className="ed-btn ed-btn-outline" onClick={() => download("letter")} disabled={!!dlBusy}>
                <FileDown size={14} strokeWidth={1.5} /> {dlBusy === "letter" ? t("ltDlBusy") : t("ltDlLetter")}
              </button>
              <button data-testid="letter-dl-package" className="ed-btn" onClick={() => download("package")} disabled={!!dlBusy}>
                <Package size={14} strokeWidth={1.5} /> {dlBusy === "package" ? t("ltDlBusy") : t("ltDlPackage")}
              </button>
              <button
                data-testid="letter-dl-package-zip"
                className="ed-btn ed-btn-outline"
                onClick={() => download("package-zip")}
                disabled={!!dlBusy}
                title={t("ltDlPackageZipTitle")}
              >
                <Package size={14} strokeWidth={1.5} /> {dlBusy === "package-zip" ? t("ltDlBusy") : t("ltDlPackageZip")}
              </button>
              <button data-testid="letter-video-open" className="ed-btn ed-btn-outline" onClick={() => setVideoOpen(true)}>
                <Video size={14} strokeWidth={1.5} /> {t("vidOpenCta")}
              </button>
              {videoMeta && (
                <span
                  data-testid="letter-video-analytics"
                  className={`inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-[0.12em] px-2.5 py-1 border ${videoMeta.source === "elevator" ? "border-amber-400 text-amber-700 bg-amber-50" : "border-emerald-400 text-emerald-800 bg-emerald-50"}`}
                  title={videoMeta.source === "elevator" ? t("vidElevatorEyebrow") : t("vidTitle")}
                >
                  {videoMeta.source === "elevator" && <><Video size={11} strokeWidth={1.7} /> {t("vidElevatorEyebrow")} · </>}
                  {t("vidViewCount", { n: videoMeta.view_count || 0 })}
                </span>
              )}
            </div>
            <div className="border-t border-neutral-200 pt-4 flex flex-wrap items-center gap-3">
              {letter.job_id ? (
                <span className="ed-eyebrow" data-testid="letter-in-register">{t("ltInRegister")}</span>
              ) : (
                <>
                  <button data-testid="letter-to-register" className="ed-btn" onClick={toRegister} disabled={registering}>
                    <BookMarked size={14} strokeWidth={1.5} /> {t("ltToRegister")}
                  </button>
                  <span className="text-xs font-mono text-neutral-500">{t("ltToRegisterHint")}</span>
                </>
              )}
            </div>
          </div>

          {/* Letter text */}
          <section>
            <div className="flex items-center justify-between gap-3 mb-1">
              <p className="ed-eyebrow">{t("ltLetter")}</p>
              {regenBusy && (
                <span
                  className="inline-flex items-center gap-2 border border-neutral-900 bg-neutral-900 text-[#F9F8F6] px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.12em]"
                  data-testid="letter-regen-badge"
                >
                  <Loader2 size={11} strokeWidth={1.8} className="animate-spin" />
                  {t("ltEnrichBusy")}
                </span>
              )}
            </div>
            <p className="text-xs font-mono text-neutral-500 mt-1 mb-3">{t("ltLetterHint")}</p>
            <textarea
              data-testid="letter-text"
              className={`ed-input min-h-[24rem] font-serif text-base leading-relaxed transition-opacity ${regenBusy ? "opacity-40 pointer-events-none" : ""}`}
              value={letter.letter_text}
              onChange={(e) => update({ letter_text: e.target.value })}
              disabled={regenBusy}
            />
          </section>

          {/* Enrich-and-regenerate box — lets the candidate feed a few
              extra sentences (extra achievements, framing hints) that
              the AI will weave into a fresh version of the letter and
              validation. Empty box = plain regenerate (existing behaviour). */}
          <EnrichRegenerateBox
            letterId={letterId}
            currentLetterText={letter.letter_text}
            onBusyChange={setRegenBusy}
            onDone={(data) => setLetter(data)}
            t={t}
          />

          {/* Validation items */}
          <section>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="ed-eyebrow">{t("ltValidation")}</p>
                <p className="text-xs font-mono text-neutral-500 mt-1">{t("ltValidationHint")}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <RegenerateValidationButton
                  letterId={letterId}
                  onRegenerated={(items) => update({ validation_items: items })}
                  disabled={!letter.resume_id}
                  t={t}
                />
                {(letter.validation_items || []).filter((x) => (x || "").trim().length >= 10).length >= 2 && (
                  <BulkPolishButton
                    items={letter.validation_items || []}
                    letterId={letterId}
                    jobTitle={letter.job_title || ""}
                    lang={letter.lang || "nl"}
                    onPolished={(next) => update({ validation_items: next })}
                    t={t}
                  />
                )}
              </div>
            </div>
            <div className="space-y-3 mt-3">
              {letter.validation_items.map((item, i) => (
                <ValidationItemRow
                  key={i}
                  index={i}
                  value={item}
                  letterId={letterId}
                  jobTitle={letter.job_title || ""}
                  lang={letter.lang || "nl"}
                  recalcKey={maximized}
                  onChange={(next) => setValItem(i, next)}
                  onDelete={() => update({ validation_items: letter.validation_items.filter((_, idx) => idx !== i) })}
                  t={t}
                />
              ))}
              <button
                data-testid="letter-val-add"
                className="ed-btn ed-btn-outline"
                onClick={() => update({ validation_items: [...letter.validation_items, ""] })}
              >
                <Plus size={14} strokeWidth={1.5} /> {t("ltAddValItem")}
              </button>
            </div>
          </section>

          <div className="sticky bottom-0 bg-[#F9F8F6] border-t border-neutral-200 -mx-8 px-8 py-4">
            <span
              data-testid="letter-autosave-status"
              className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] inline-flex items-center gap-1.5 ${saving || dirty ? "text-neutral-500" : "text-emerald-700"}`}
            >
              {saving ? t("rbAutosaving") : dirty ? t("rbAutosavePending") : (<><Check size={11} strokeWidth={2} /> {t("rbAutosaved")}</>)}
            </span>
          </div>
        </div>
      </div>
      {improveOpen && (
        <ImproveMatchWizard
          letterId={letterId}
          letterJobTitle={letter.job_title || ""}
          currentScore={letter.match_score}
          onLetterUpdated={(fresh) => { setLetter(fresh); didChangeRef.current = true; }}
          onClose={() => setImproveOpen(false)}
        />
      )}
      {videoOpen && (
        <VideoLetterModal letter={letter} onClose={() => { setVideoOpen(false); refreshVideoMeta(); }} />
      )}
      {translationEditorLang && (
        <TranslationEditor
          letterId={letterId}
          targetLang={translationEditorLang}
          sourceLang={letter?.lang || "nl"}
          onSaved={() => { didChangeRef.current = true; }}
          onClose={() => setTranslationEditorLang(null)}
        />
      )}
      <DownloadOverlay busyKind={dlBusy} />
    </div>
  );
}

/**
 * "Verrijk deze brief" box — a compact textarea + button rendered directly
 * under the generated letter. Lets the candidate feed extra authentic
 * details (a project they forgot to add, a framing hint, an extra
 * achievement) and regenerates the letter + validation with those hints
 * woven in. Empty box + click = plain regenerate (existing behaviour).
 *
 * Keeps its own local state so parent re-renders don't lose the draft.
 * On success the box clears and the LetterEditor is fed the fresh doc.
 */
function EnrichRegenerateBox({ letterId, currentLetterText, onDone, onBusyChange, t }) {
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  // Preview state — populated from the AI once the user clicks "Verwerk".
  // Renders as a modal with the current version (read-only, for reference)
  // next to an EDITABLE textarea containing the AI output. Users can
  // hand-tune the fresh text before committing, so the flow doubles as a
  // "generate → tweak → apply" instead of a raw take-it-or-leave-it.
  const [preview, setPreview] = useState(null);  // { letter_text, validation_items, match_score, match_summary }
  const [previewDirty, setPreviewDirty] = useState(false);
  const disabled = busy || applying || !letterId;

  const submit = async () => {
    if (disabled) return;
    setBusy(true);
    onBusyChange?.(true);
    try {
      // Two-phase flow: request a preview (no DB write, no PDF-cache
      // invalidation, no match_ai quota bump). User confirms in the
      // modal → apply-phase persists via PATCH /letters/{lid}. Only
      // the AI call is billed either way, but a rejected preview
      // leaves the saved letter untouched.
      const { data } = await api.post(
        `/letters/${letterId}/regenerate`,
        { extra_context: extra || "", preview_only: true },
        { timeout: 140000 },
      );
      setPreview(data?.preview || null);
      setPreviewDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ltEnrichFailed"));
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  const applyPreview = async () => {
    if (!preview || applying) return;
    setApplying(true);
    try {
      // Persist via the existing PUT so downstream (PDF cache, match
      // history, updated_at) all get refreshed the same way an editorial
      // save does.
      const { data } = await api.put(`/letters/${letterId}`, {
        letter_text: preview.letter_text,
        validation_items: preview.validation_items,
        match_score: preview.match_score,
        match_summary: preview.match_summary,
      });
      toast.success(t("ltEnrichSuccess"));
      setExtra("");
      setPreview(null);
      setPreviewDirty(false);
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ltEnrichFailed"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <section
      className="ed-card px-5 py-4"
      data-testid="letter-enrich-box"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="ed-eyebrow inline-flex items-center gap-2">
            <Sparkles size={11} strokeWidth={1.6} /> {t("ltEnrichTitle")}
          </p>
          <p className="text-xs font-mono text-neutral-500 mt-1">
            {t("ltEnrichHint")}
          </p>
        </div>
      </div>
      <textarea
        data-testid="letter-enrich-input"
        className="ed-input mt-3 min-h-[6rem] text-sm leading-relaxed"
        placeholder={t("ltEnrichPlaceholder")}
        value={extra}
        onChange={(e) => setExtra(e.target.value)}
        disabled={busy}
        maxLength={1200}
      />
      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
        <span
          className="text-[0.6rem] font-mono uppercase tracking-[0.12em] text-neutral-500"
          data-testid="letter-enrich-count"
        >
          {extra.length}/1200
        </span>
        <button
          type="button"
          data-testid="letter-enrich-submit"
          onClick={submit}
          disabled={disabled}
          className="ed-btn inline-flex items-center gap-2"
        >
          {busy
            ? <><Loader2 size={13} strokeWidth={1.7} className="animate-spin" /> {t("ltEnrichBusy")}</>
            : <><Sparkles size={13} strokeWidth={1.6} /> {t("ltEnrichCta")}</>}
        </button>
      </div>

      {/* Confirm-preview modal — shows the fresh AI output next to the
          current saved letter so the user can decide whether it's an
          improvement worth committing. Uses a plain fixed overlay
          (matches VideoLetterModal / other confirms in this codebase). */}
      {preview && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
          data-testid="letter-enrich-preview-modal"
          onClick={() => !applying && setPreview(null)}
        >
          <div
            className="bg-[#F9F8F6] border border-neutral-900 w-full max-w-5xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-neutral-200">
              <div>
                <p className="ed-eyebrow inline-flex items-center gap-2">
                  <Sparkles size={11} strokeWidth={1.6} /> {t("ltEnrichPreviewEyebrow")}
                </p>
                <h3 className="font-serif text-2xl mt-1">{t("ltEnrichPreviewTitle")}</h3>
              </div>
              <button
                type="button"
                data-testid="letter-enrich-preview-close"
                onClick={() => !applying && setPreview(null)}
                disabled={applying}
                className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-6 overflow-y-auto flex-1">
              <div>
                <p className="ed-eyebrow mb-2">{t("ltEnrichPreviewCurrent")}</p>
                <div
                  className="ed-card px-4 py-3 font-serif text-sm leading-relaxed whitespace-pre-wrap max-h-[60vh] overflow-y-auto text-neutral-500"
                  data-testid="letter-enrich-preview-current"
                >
                  {currentLetterText || "—"}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="ed-eyebrow">{t("ltEnrichPreviewNew")}</p>
                  {previewDirty && (
                    <span
                      className="font-mono text-[0.55rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-neutral-900 bg-neutral-100 text-neutral-700"
                      data-testid="letter-enrich-preview-dirty"
                    >
                      {t("ltEnrichPreviewEditedBadge")}
                    </span>
                  )}
                </div>
                <textarea
                  data-testid="letter-enrich-preview-new"
                  className="ed-input font-serif text-sm leading-relaxed w-full h-[60vh]"
                  value={preview.letter_text || ""}
                  onChange={(e) => {
                    setPreview({ ...preview, letter_text: e.target.value });
                    setPreviewDirty(true);
                  }}
                  disabled={applying}
                />
                <p className="text-[0.6rem] font-mono uppercase tracking-[0.12em] text-neutral-500 mt-1">
                  {t("ltEnrichPreviewEditHint")}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-neutral-200">
              <p className="text-xs font-mono text-neutral-500">
                {t("ltEnrichPreviewFooterHint")}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="letter-enrich-preview-reject"
                  onClick={() => setPreview(null)}
                  disabled={applying}
                  className="ed-btn ed-btn-outline"
                >
                  {t("ltEnrichPreviewReject")}
                </button>
                <button
                  type="button"
                  data-testid="letter-enrich-preview-apply"
                  onClick={applyPreview}
                  disabled={applying}
                  className="ed-btn inline-flex items-center gap-2"
                >
                  {applying
                    ? <><Loader2 size={13} strokeWidth={1.7} className="animate-spin" /> {t("ltEnrichBusy")}</>
                    : <><Check size={13} strokeWidth={1.7} /> {t("ltEnrichPreviewApply")}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * One row of the Eisen-Validatie list. Combines the numbered auto-grow
 * textarea, the delete button, and a small AI "Herschrijf" button
 * that hits `/letters/{lid}/validation/polish` to smooth out the flow
 * after manual edits. Undo-toast so a bad rewrite is trivially
 * recoverable.
 */
function ValidationItemRow({ index, value, letterId, jobTitle, lang, recalcKey, onChange, onDelete, t }) {
  const [aiBusy, setAiBusy] = useState(false);
  const polish = async () => {
    const text = (value || "").trim();
    if (text.length < 10) {
      toast.error(t("ltValPolishTooShort"));
      return;
    }
    setAiBusy(true);
    const before = value;
    try {
      const { data } = await api.post(`/letters/${letterId}/validation/polish`, {
        text,
        job_title: jobTitle || "",
        lang: lang || "nl",
      });
      const polished = (data?.polished || "").trim();
      if (!polished || polished === text) {
        toast.info(t("ltValPolishNoChange"));
        return;
      }
      onChange(polished);
      toast.success(t("ltValPolishDone"), {
        action: {
          label: t("ltValPolishUndo"),
          onClick: () => onChange(before),
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ltValPolishFail"));
    } finally {
      setAiBusy(false);
    }
  };
  return (
    <div className="flex gap-2 items-start" data-testid={`letter-val-row-${index}`}>
      <span className="ed-num shrink-0 mt-3">{index + 1}.</span>
      <div className="flex-1 min-w-0 space-y-1.5">
        {/* Eis title — bold single-line so the requirement itself
            reads at a glance. Stored + emitted joined back to the
            single-string format via " — " to stay wire-compatible
            with the backend. */}
        <ValidationTitleField
          value={parseValItem(value).title}
          onChange={(nextTitle) => onChange(joinValItem(nextTitle, parseValItem(value).body))}
          index={index}
          t={t}
        />
        <AutoGrowTextarea
          testId={`letter-val-${index}`}
          value={parseValItem(value).body}
          recalcKey={recalcKey}
          onChange={(e) => onChange(joinValItem(parseValItem(value).title, e.target.value))}
        />
      </div>
      <button
        type="button"
        data-testid={`letter-val-polish-${index}`}
        onClick={polish}
        disabled={aiBusy || (value || "").trim().length < 10}
        title={t("ltValPolishHint")}
        aria-label={t("ltValPolishCta")}
        className="p-2 mt-8 border border-neutral-300 text-neutral-500 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
      >
        {aiBusy ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Sparkles size={13} strokeWidth={1.5} />}
      </button>
      <button
        data-testid={`letter-val-delete-${index}`}
        onClick={onDelete}
        className="p-2 mt-8 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors shrink-0"
      >
        <Trash2 size={13} strokeWidth={1.5} />
      </button>
    </div>
  );
}

/** Splits a validation item on the first " — " (em-dash w/ spaces) or
 *  " - " fallback into {title, body}. Everything before the separator
 *  is the requirement/wens (rendered bold); everything after is the
 *  motivation. Idempotent — joining back yields the same string when
 *  neither field was changed. */
function parseValItem(raw) {
  const s = (raw || "").trim();
  if (!s) return { title: "", body: "" };
  // Prefer em-dash (curly). Fall back to hyphen-minus with spaces so
  // legacy items still split correctly.
  let idx = s.indexOf(" — ");
  let sepLen = 3;
  if (idx === -1) {
    idx = s.indexOf(" - ");
    sepLen = 3;
  }
  if (idx === -1) return { title: "", body: s };
  return { title: s.slice(0, idx).trim(), body: s.slice(idx + sepLen).trim() };
}

function joinValItem(title, body) {
  const t = (title || "").trim();
  const b = (body || "").trim();
  if (!t && !b) return "";
  if (!t) return b;
  if (!b) return t;
  return `${t} — ${b}`;
}

function ValidationTitleField({ value, onChange, index, t }) {
  return (
    <input
      type="text"
      data-testid={`letter-val-title-${index}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t("ltValTitlePh")}
      className="w-full font-serif text-base font-semibold text-neutral-900 bg-transparent border-0 border-b border-transparent focus:border-neutral-900 outline-none px-0 py-1 placeholder:font-normal placeholder:italic placeholder:text-neutral-400"
    />
  );
}

/**
 * "Polijst alle eisen" — batches a single AI call across every
 * validation item ≥10 chars, then swaps the results back atomically.
 * Snapshots the pre-polish array so a bad rewrite can be undone with
 * one toast click.
 */
function BulkPolishButton({ items, letterId, jobTitle, lang, onPolished, t }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    const before = [...(items || [])];
    setBusy(true);
    try {
      const { data } = await api.post(`/letters/${letterId}/validation/polish/bulk`, {
        items: before,
        job_title: jobTitle || "",
        lang: lang || "nl",
      });
      const next = Array.isArray(data?.polished) ? data.polished : null;
      if (!next || next.length !== before.length) {
        toast.error(t("ltValPolishBulkFail"));
        return;
      }
      // No-op detection: if nothing meaningfully changed, tell the user
      // instead of showing a "polished!" celebration.
      const changedCount = next.reduce(
        (n, v, i) => n + ((v || "").trim() !== (before[i] || "").trim() ? 1 : 0),
        0,
      );
      if (changedCount === 0) {
        toast.info(t("ltValPolishBulkNoChange"));
        return;
      }
      onPolished(next);
      toast.success(t("ltValPolishBulkDone").replace("{n}", changedCount), {
        action: {
          label: t("ltValPolishUndo"),
          onClick: () => onPolished(before),
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ltValPolishBulkFail"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid="letter-val-polish-bulk"
      onClick={run}
      disabled={busy}
      className="ed-btn ed-btn-outline shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
      title={t("ltValPolishBulkHint")}
    >
      {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
      {busy ? t("ltValPolishBulkBusy") : t("ltValPolishBulkCta")}
    </button>
  );
}


/**
 * "Herbereken eisen-validatie" — regenerates the full validation list
 * against the currently-linked CV. Includes a depth toggle:
 *
 *   'deep'    → per-eis cross-reference: elke werkervaring waarin de
 *               eis voorkomt wordt genoemd met bedrijf, periode en
 *               concreet voorbeeld. Beknopte motivatie i.p.v. één
 *               algemene zin.
 *   'concise' → 1-2 zinnen per eis (klassieke stijl).
 *
 * Snapshots the pre-regen list so a bad rewrite can be undone via the
 * toast's undo action.
 */
function RegenerateValidationButton({ letterId, onRegenerated, disabled, t }) {
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [depth, setDepth] = useState("deep");
  // Compare-modal state: after the AI returns, we DON'T auto-apply.
  // We stage the diff and let the user pick which items to accept
  // per-row. `compare.before` is the pre-regen list; `compare.after`
  // is what the AI produced; both are always the same length in the
  // UI (we pad with "" if needed so users can add net-new items).
  const [compare, setCompare] = useState(null); // {before, after, depth}
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const run = async (nextDepth) => {
    if (busy) return;
    setDepth(nextDepth);
    setMenuOpen(false);
    setBusy(true);
    try {
      const { data: pre } = await api.get(`/letters/${letterId}`);
      const before = Array.isArray(pre?.validation_items) ? [...pre.validation_items] : [];
      const { data } = await api.post(`/letters/${letterId}/validation/regenerate`, { depth: nextDepth });
      const after = Array.isArray(data?.validation_items) ? data.validation_items : null;
      if (!after || after.length === 0) {
        toast.error(t("ltValRegenFail"));
        return;
      }
      // The backend already persisted `after` to the DB. To honour a
      // "keep old" cancel, we roll the letter back to `before` if the
      // user rejects everything in the compare modal (see cancel path).
      setCompare({ before, after, depth: nextDepth });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ltValRegenFail"));
    } finally {
      setBusy(false);
    }
  };

  const closeCompare = () => setCompare(null);

  const applySelection = async (selected) => {
    // `selected` = the final list of validation strings after user
    // resolved each row (kept old | accepted new | edited).
    if (!compare) return;
    try {
      // Persist the user's chosen mix. The backend's letter update
      // route is PUT (not PATCH — different from /api/jobs which uses
      // PATCH). Using the wrong verb bounces off 405 and silently
      // discards the compare-modal choices.
      await api.put(`/letters/${letterId}`, { validation_items: selected });
      onRegenerated(selected);
      const changed = selected.reduce(
        (n, v, i) => n + (((compare.before[i] || "").trim() !== (v || "").trim()) ? 1 : 0),
        0,
      );
      toast.success(t("ltValCompareApplied").replace("{n}", changed));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ltValCompareApplyFail"));
    } finally {
      setCompare(null);
    }
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        data-testid="letter-val-regen-btn"
        onClick={() => (busy ? null : setMenuOpen((v) => !v))}
        disabled={busy || disabled}
        className="ed-btn ed-btn-outline shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        title={disabled ? t("ltValRegenNoCv") : t("ltValRegenHint")}
      >
        {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <RefreshCw size={14} strokeWidth={1.5} />}
        {busy ? t("ltValRegenBusy") : t("ltValRegenCta")}
        {!busy && <ChevronDown size={12} strokeWidth={1.75} className="opacity-60" />}
      </button>
      {menuOpen && !busy && (
        <div
          data-testid="letter-val-regen-menu"
          // Anchor the menu to the LEFT edge of the button and open
          // rightward. The letter-editor sheet has overflow-y-auto
          // and horizontal clipping, so a right-anchored dropdown
          // (`right-0`) sticks its left edge OUT of the visible sheet
          // and appears half-cut. Left-anchor + high z-index keeps
          // the menu fully readable, and covers the neighbouring
          // "Polijst alle eisen" button while it's open (which is
          // fine — it disappears on click-outside).
          className="absolute left-0 top-full mt-1 min-w-[280px] bg-[#F9F8F6] border border-neutral-900 shadow-xl z-50"
        >
          <button
            type="button"
            data-testid="letter-val-regen-deep"
            onClick={() => run("deep")}
            className="block w-full text-left px-4 py-3 hover:bg-neutral-100 border-b border-neutral-200"
          >
            <div className="flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-900">
              <Sparkles size={12} strokeWidth={1.75} /> {t("ltValRegenDeepLabel")}
            </div>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{t("ltValRegenDeepHint")}</p>
          </button>
          <button
            type="button"
            data-testid="letter-val-regen-concise"
            onClick={() => run("concise")}
            className="block w-full text-left px-4 py-3 hover:bg-neutral-100"
          >
            <div className="flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-900">
              <RefreshCw size={12} strokeWidth={1.75} /> {t("ltValRegenConciseLabel")}
            </div>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{t("ltValRegenConciseHint")}</p>
          </button>
        </div>
      )}
      {compare && (
        <ValidationCompareModal
          before={compare.before}
          after={compare.after}
          depth={compare.depth}
          onCancel={closeCompare}
          onApply={applySelection}
          t={t}
        />
      )}
    </div>
  );
}


/**
 * Side-by-side compare modal for the eisen-validatie regeneration.
 * Left column shows the OLD text, right column shows the AI's NEW
 * proposal — one row per requirement. For each row the user picks
 * one of three actions:
 *
 *   - keep-old    (default when both differ)
 *   - accept-new  (checkbox on the right column)
 *   - edit-new    (the right textarea is editable inline)
 *
 * When rows in `after` extend beyond `before.length` we treat them
 * as net-new items — the left column is intentionally empty.
 * When `before` is longer than `after` (AI merged/dropped items) the
 * extra old rows are shown with "AI heeft dit item weggelaten" and
 * default kept — no silent deletion.
 */
function ValidationCompareModal({ before, after, depth, onCancel, onApply, t }) {
  const rows = Math.max(before.length, after.length);
  const [choices, setChoices] = useState(() => {
    // Default: accept every new item that differs from the old.
    const c = {};
    for (let i = 0; i < rows; i++) {
      const o = (before[i] || "").trim();
      const n = (after[i] || "").trim();
      if (i >= before.length) c[i] = "accept"; // net-new item — accept by default
      else if (i >= after.length) c[i] = "keep"; // AI dropped it — keep original
      else if (!o) c[i] = "accept"; // was empty, AI filled it
      else if (o === n) c[i] = "same"; // AI returned identical text
      else c[i] = "accept";
    }
    return c;
  });
  const [edits, setEdits] = useState({}); // {rowIdx: text}

  const setChoice = (i, v) => setChoices((s) => ({ ...s, [i]: v }));
  const setEdit = (i, v) => setEdits((s) => ({ ...s, [i]: v }));

  const summary = (() => {
    let accepted = 0, kept = 0, edited = 0, dropped = 0, same = 0;
    for (let i = 0; i < rows; i++) {
      const c = choices[i];
      if (c === "accept" && edits[i] !== undefined && edits[i] !== (after[i] || "")) edited++;
      else if (c === "accept") accepted++;
      else if (c === "keep") kept++;
      else if (c === "same") same++;
      else if (c === "drop") dropped++;
    }
    return { accepted, kept, edited, dropped, same };
  })();

  const handleApply = () => {
    // Materialize the final list based on each row's decision.
    const out = [];
    for (let i = 0; i < rows; i++) {
      const c = choices[i];
      if (c === "drop") continue;
      if (c === "keep") out.push(before[i] || "");
      else if (c === "same") out.push(after[i] || "");
      else if (c === "accept") out.push(edits[i] !== undefined ? edits[i] : (after[i] || ""));
    }
    onApply(out.filter((x) => (x || "").trim().length > 0));
  };

  const acceptAll = () => {
    const c = {};
    for (let i = 0; i < rows; i++) c[i] = i >= after.length ? "keep" : "accept";
    setChoices(c);
  };
  const keepAll = () => {
    const c = {};
    for (let i = 0; i < rows; i++) c[i] = i >= before.length ? "drop" : "keep";
    setChoices(c);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      data-testid="letter-val-compare-modal"
    >
      <div className="bg-[#F9F8F6] border border-neutral-900 max-w-6xl w-full max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-4 p-6 border-b border-neutral-200">
          <div className="min-w-0">
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700 flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.75} />
              {t("ltValCompareEyebrow")} · {depth === "deep" ? t("ltValRegenDeepLabel") : t("ltValRegenConciseLabel")}
            </p>
            <h3 className="ed-h2 mt-1 leading-tight">{t("ltValCompareTitle")}</h3>
            <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{t("ltValCompareBody")}</p>
          </div>
          <button
            type="button"
            data-testid="letter-val-compare-close"
            onClick={onCancel}
            className="shrink-0 text-neutral-500 hover:text-neutral-900 transition-colors p-1"
            aria-label={t("cancel")}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-neutral-200 bg-neutral-100">
          <button type="button" data-testid="letter-val-compare-accept-all" onClick={acceptAll} className="ed-btn ed-btn-outline">
            <Check size={12} strokeWidth={1.75} /> {t("ltValCompareAcceptAll")}
          </button>
          <button type="button" data-testid="letter-val-compare-keep-all" onClick={keepAll} className="ed-btn ed-btn-outline">
            <RefreshCw size={12} strokeWidth={1.75} /> {t("ltValCompareKeepAll")}
          </button>
          <div className="ml-auto font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-600">
            {t("ltValCompareStats")
              .replace("{acc}", summary.accepted + summary.edited)
              .replace("{keep}", summary.kept + summary.same)
              .replace("{drop}", summary.dropped)}
          </div>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-neutral-200">
          <div className="grid grid-cols-[3rem_1fr_1fr] gap-3 px-6 py-3 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 bg-neutral-50 sticky top-0 z-10 border-b border-neutral-200">
            <div>#</div>
            <div>{t("ltValCompareColOld")}</div>
            <div>{t("ltValCompareColNew")}</div>
          </div>
          {Array.from({ length: rows }).map((_, i) => {
            const oldText = before[i] || "";
            const newText = after[i] || "";
            const isSame = oldText.trim() === newText.trim() && oldText.trim();
            const isNetNew = i >= before.length;
            const isDropped = i >= after.length;
            const c = choices[i];
            return (
              <div key={i} className="grid grid-cols-[3rem_1fr_1fr] gap-3 px-6 py-4 items-start" data-testid={`letter-val-compare-row-${i}`}>
                <div className="font-mono text-xs text-neutral-500 pt-1">{i + 1}.</div>
                {/* OLD column */}
                <div>
                  {isNetNew ? (
                    <p className="text-xs italic text-neutral-400 leading-relaxed">
                      {t("ltValCompareOldEmptyNetNew")}
                    </p>
                  ) : (
                    <>
                      <p className={`text-sm leading-relaxed whitespace-pre-wrap ${c === "keep" ? "text-neutral-900" : "text-neutral-500 line-through decoration-neutral-400/60"}`}>
                        {oldText}
                      </p>
                      {!isDropped && !isSame && (
                        <button
                          type="button"
                          data-testid={`letter-val-compare-keep-${i}`}
                          onClick={() => setChoice(i, "keep")}
                          className={`mt-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 border ${c === "keep" ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-600 hover:border-neutral-900"}`}
                        >
                          {t("ltValCompareKeepThis")}
                        </button>
                      )}
                      {isDropped && (
                        <p className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-amber-800 bg-amber-100 border border-amber-300 px-2 py-1 inline-block">
                          {t("ltValCompareAiDropped")}
                        </p>
                      )}
                    </>
                  )}
                </div>
                {/* NEW column */}
                <div>
                  {isDropped ? (
                    <p className="text-xs italic text-neutral-400 leading-relaxed">
                      {t("ltValCompareNewEmpty")}
                    </p>
                  ) : (
                    <>
                      {isSame ? (
                        <p className="text-sm text-neutral-500 italic leading-relaxed">
                          {t("ltValCompareUnchanged")}
                        </p>
                      ) : (
                        <textarea
                          data-testid={`letter-val-compare-new-input-${i}`}
                          value={edits[i] !== undefined ? edits[i] : newText}
                          onChange={(e) => setEdit(i, e.target.value)}
                          rows={Math.min(8, Math.max(3, Math.ceil((edits[i] ?? newText).length / 80)))}
                          className={`w-full text-sm leading-relaxed resize-y bg-white border px-3 py-2 outline-none ${c === "accept" ? "border-emerald-700" : "border-neutral-200 opacity-60"}`}
                          disabled={c !== "accept"}
                        />
                      )}
                      {!isSame && (
                        <button
                          type="button"
                          data-testid={`letter-val-compare-accept-${i}`}
                          onClick={() => setChoice(i, "accept")}
                          className={`mt-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 border ${c === "accept" ? "bg-emerald-700 text-white border-emerald-700" : "border-emerald-700 text-emerald-800 hover:bg-emerald-50"}`}
                        >
                          {t("ltValCompareTakeNew")}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-neutral-200 bg-neutral-50">
          <button type="button" data-testid="letter-val-compare-cancel" onClick={onCancel} className="ed-btn ed-btn-outline">
            {t("cancel")}
          </button>
          <button type="button" data-testid="letter-val-compare-apply" onClick={handleApply} className="ed-btn">
            <Check size={13} strokeWidth={1.75} /> {t("ltValCompareApply")}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Full-screen download overlay — surfaces the current download op as a
 * centered card so the user never wonders "did anything happen?".
 * PDF/ZIP generation can take 5-30s, and the tiny "Bezig…" swap on a
 * single button was too subtle. Backdrop is semi-transparent so users
 * can still see context, but click-through is blocked (pointer-events)
 * so they can't accidentally trigger a second download mid-flight.
 */
function DownloadOverlay({ busyKind }) {
  const { t } = useT();
  if (!busyKind) return null;
  const labelKey = {
    resume: "ltDlOverlayResume",
    letter: "ltDlOverlayLetter",
    validation: "ltDlOverlayValidation",
    package: "ltDlOverlayPackage",
    "package-zip": "ltDlOverlayPackageZip",
  }[busyKind] || "ltDlOverlayGeneric";
  return (
    <div
      data-testid="letter-download-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-900/40 backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <div className="bg-white border border-neutral-900 shadow-xl px-8 py-6 flex items-center gap-4 max-w-sm">
        <Loader2 size={28} strokeWidth={1.5} className="animate-spin text-neutral-900 shrink-0" />
        <div className="min-w-0">
          <p
            data-testid="letter-download-overlay-label"
            className="font-serif text-lg text-neutral-900 leading-tight"
          >
            {t(labelKey)}
          </p>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mt-1">
            {t("ltDlOverlayHint")}
          </p>
        </div>
      </div>
    </div>
  );
}

function MatchAiUsageHint() {
  const { t } = useT();
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    api.get("/letters/match-ai-usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  if (!usage || usage.unlimited || usage.remaining === null) return null;
  const low = usage.remaining <= 3;
  return (
    <p
      data-testid="letter-improve-usage-hint"
      className={`font-mono text-[0.6rem] uppercase tracking-[0.15em] ${low ? "text-amber-700" : "text-neutral-500"}`}
    >
      {t("ltImproveUsage").replace("{r}", usage.remaining).replace("{l}", usage.limit)}
    </p>
  );
}

